import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { authDO, checkAuthDO, refreshAuthDO } from 'utils/authUtils';
import { getMyRolesDO, getRolesDO } from 'utils/rolesUtils';
import { getBrandsDO } from 'utils/brandsUtils';
import { BoUser } from 'models/BO/BoUser';
import { Brand } from 'models/BO/Brand';
import { BoRole, Permission } from 'models/BO/BoRole';
import { groupBy } from 'utils/globalsUtils';
import { networkGlobals } from 'utils/networkUtils';
import { changeFavicon } from 'utils/globalsUtils';
import { loadSettings } from 'utils/settingsUtils';
import { useBrands } from './useBrands';

export interface AuthState {
  currentUser: BoUser | null;
  myRoles: BoRole[];
  currentRole: Permission;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface UseAuthReturn extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  updateUser: (user: BoUser) => void;
  initializeAuth: () => Promise<void>;
  prepareRoles: (myRoles: BoRole[], currentBrand: Brand) => void;

}

export const AuthContext = createContext<UseAuthReturn >({
    currentUser: null,
    myRoles: [],
    currentRole: new Permission(),
      isAuthenticated: false,
      isLoading: false,
      login: () => Promise.resolve({ success: false }),
      logout: () => {},
      refreshAuth: () => Promise.resolve(),
      updateUser: () => {},
      initializeAuth: () => Promise.resolve(),
      prepareRoles: () => {},
});
export const useAuth = (): UseAuthReturn => {
  const {
    currentUser,
    myRoles,
    currentRole,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshAuth,
    updateUser,
    initializeAuth,
    prepareRoles,
  } = useContext<UseAuthReturn>(AuthContext);

  return {
    currentUser,
    myRoles,
    currentRole,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshAuth,
    updateUser,
    initializeAuth,
    prepareRoles,
  };
}; 


export function AuthProvider({ children }) {

  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    currentUser: null,
    myRoles: [],
    currentRole: new Permission(),
    isAuthenticated: false,
    isLoading: false,
  });
 

  const { setBrands} = useBrands();
  // Initialize auth state from localStorage
  const initializeAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('BOToken');
      const userData = localStorage.getItem('BOCurrentUser');

      if (!token || !userData) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // Check if token is valid
      try {
        await checkAuthDO();
      } catch (error) {
        // Token might be expired, try to refresh
        try {
          await refreshAuthDO();
        } catch (refreshError) {
          // Refresh failed, clear auth state
          localStorage.removeItem('BOToken');
          localStorage.removeItem('BOCurrentUser');
          localStorage.removeItem('currentBrand');
          setState(prev => ({ ...prev, isLoading: false }));
          return;
        }
      }

      // Load user data
      const user = Object.assign(new BoUser(), JSON.parse(userData));
      
      // Load brands and roles
      const roles = await getMyRolesDO();
      localStorage.setItem(`BORoles`, JSON.stringify(roles));

      setBrands(user?.brands);
      setState({
        currentUser: user,
        currentRole: new Permission(),
        myRoles: roles,
        isAuthenticated: true,
        isLoading: false,
      });

    } catch (error) {
      console.error('Failed to initialize auth:', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  // Prepare roles for a specific brand
  const prepareRoles = useCallback((myRoles: BoRole[],currentBrand: Brand) => {

    const rolesByBrand: { [key: string]: BoRole[] } = groupBy(myRoles, (x) => x.brandId);
    const brandRoles = rolesByBrand[currentBrand.id];

    if (!brandRoles) return null;

    let pages: number[] = [];
    let controls: any[] = [];

    for (const role of brandRoles) {
      for (const r of role?.permissions?.pages || []) {
        pages.push(r);
        pages.push(Math.floor(r));
        if (!Number.isSafeInteger(r)) {
          const control = role?.permissions?.controls?.find((rr) => rr?.page === r);
          if (control) {
            controls.push(control);
          }
        }
      }
    }

    const role = { pages, controls };
   
    setState(prev => ({ ...prev, currentRole: Object.assign(new Permission(), role) }));
  }, []);

  // Login function
  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      const user = await  authDO(email, password, 'dev')

      if (!user?.user?.email) {
        throw new Error('Invalid response from server');
      }

      const newUser = user.user;
      const token = user.token;

      // Set token
      localStorage.setItem('BOToken', token);
      localStorage.setItem('BOCurrentUser', JSON.stringify(newUser));
      setBrands(newUser?.brands);
      
      const roles = await getMyRolesDO();
      localStorage.setItem(`BORoles`, JSON.stringify(roles));

      setState({
        currentUser: Object.assign(new BoUser(), newUser),
        currentRole: new Permission(),
        myRoles: roles,
        isAuthenticated: true,
        isLoading: false,
      });

      // Handle redirect
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        localStorage.removeItem('redirectAfterLogin');
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1000);
      } else {
        navigate('/dashboard');
      }

      return { success: true };

    } catch (error: any) {
      setState(prev => ({ ...prev, isLoading: false }));
      
      if (error?.response?.status === 400 || error?.response?.status === 404) {
        return { success: false, error: 'Incorrect Email/Password' };
      }
      
      return { success: false, error: error?.message || 'Login failed' };
    }
  }, [navigate, prepareRoles]);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('BOToken');
    localStorage.removeItem('BOCurrentUser');
    localStorage.removeItem('currentBrand');
    
    // Clear role data for all brands
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('BORoles')) {
        localStorage.removeItem(key);
      }
    });

    setState({
      currentUser: null,
      currentRole: new Permission(),
      isAuthenticated: false,
      myRoles: [],
      isLoading: false,
    });

    navigate('/sign-in');
  }, [navigate]);

  // Refresh auth function
  const refreshAuth = useCallback(async () => {
    try {
      await refreshAuthDO();
      await initializeAuth();
    } catch (error) {
      console.error('Failed to refresh auth:', error);
      logout();
    }
  }, [initializeAuth, logout]);

  const updateUser = useCallback((user: BoUser) => {
    localStorage.setItem('BOCurrentUser', JSON.stringify(user));
    setState(prev => ({ ...prev, currentUser: Object.assign(new BoUser(), user) }));
  }, []);


  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      refreshAuth,
      updateUser,
      initializeAuth,
      prepareRoles,
     }}>
      {children}
    </AuthContext.Provider>
  );
}
